import type { SeatingParty } from '@peles/shared';
import { stillToSeat } from './boardModel';

/**
 * The names of what is dragged and where it is dropped, for the seating board
 * and the floor plan's Seating mode alike, and what a drop means.
 */

/** The drag id of a party on the board: one per party, wherever it shows. */
export function partyDragId(guestId: string, where: string): string {
  return `party:${guestId}:${where}`;
}

/** The party a drag id names. */
export function guestOfDragId(id: string | number): string | null {
  const [kind, guestId] = String(id).split(':');
  return kind === 'party' && guestId ? guestId : null;
}

/** The drop target id of a table on the board. */
export function tableDropId(uid: string): string {
  return `table:${uid}`;
}

/** The table a drop id names. */
export function tableOfDropId(id: string | number): string | null {
  const text = String(id);
  return text.startsWith('table:') ? text.slice('table:'.length) : null;
}

/** The drop target id of one seat at a table (a chair on the floor plan). */
export function seatDropId(uid: string, seatNumber: number): string {
  return `seat:${seatNumber}:${uid}`;
}

/** The table and seat a seat drop id names. */
export function seatOfDropId(id: string | number): { tableUid: string; seatNumber: number } | null {
  const match = /^seat:(\d+):(.+)$/.exec(String(id));
  return match ? { tableUid: match[2], seatNumber: Number(match[1]) } : null;
}

/** The drop target that takes a party off the plan. */
export const UNSEATED_DROP_ID = 'unseated';

/**
 * What a drop means: a party onto a table or one of its seats, or a seated
 * party back onto the unseated list. Null when it changes nothing — dropped
 * outside a target, a whole party back where it sits, or an unseated party
 * onto the unseated list.
 */
export function resolveSeatingDrop(
  parties: Map<string, SeatingParty>,
  activeId: string | number,
  overId: string | number | undefined,
): { guestId: string; tableUid: string | null; seatNumber?: number } | null {
  const guestId = guestOfDragId(activeId);
  if (!guestId || overId == null) return null;
  const party = parties.get(guestId);
  if (!party) return null;
  const seat = seatOfDropId(overId);
  if (seat) return { guestId, ...seat };
  const tableUid = tableOfDropId(overId);
  if (tableUid) {
    const alreadyThere =
      party.tableUids.length === 1 && party.tableUids[0] === tableUid && stillToSeat(party) === 0;
    return alreadyThere ? null : { guestId, tableUid };
  }
  if (overId === UNSEATED_DROP_ID && party.seatsHeld > 0) return { guestId, tableUid: null };
  return null;
}
